<script lang="ts">
export default {
  name: 'KkButton',
}
</script>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { KkIcon } from '../'
import type { RouteLocationRaw } from 'vue-router'
import type { IconName } from '../KkIcon/index.vue'

export type ButtonColor = 'primary' | 'primaryOutlined' | 'secondary'

export interface Props {
  text: string
  href?: string
  to?: RouteLocationRaw
  color?: ButtonColor
  disabled?: boolean
  iconName?: IconName
  full?: boolean
  height?: string
  fontBold?: boolean
}

export interface Emits {
  (key: 'click', event: MouseEvent): void
}

const props = withDefaults(defineProps<Props>(), {
  color: 'primary',
  height: '44px',
  fontBold: true,
})
const emits = defineEmits<Emits>()
const isHover = ref(false)

const tag = computed(() => {
  if (props.href && props.to) throw new Error('Invalid props set. It can\'t has both \'href\' and \'to\'.')

  if (props.href) return 'a'
  if (props.to) {
    return props.disabled ? 'span' : 'router-link'
  }
  return 'button'
})

const buttonClass = computed(() => {
  return {
    [`button--${props.color}`]: true,
    ['button--disabled']: props.disabled,
    ['button--full']: props.full,
  }
})

const styles = computed(() => {
  const styles: Record<string, string> = {}
  styles['height'] = props.height
  if(props.fontBold) styles['font-weight'] = 'bold'
  return styles
})

const target = computed(() => {
  return tag.value === 'a' ? '_blank' : undefined
})

const iconColor = computed(() => {
  let color = '#FFF'
  if (props.color === 'primaryOutlined') {
    color = isHover.value ? '#FFF' : '#E9446A'
  }
  if (props.color === 'secondary') {
    color = isHover.value ? '#FFF' : '#6D7381'
  }
  if (props.disabled) color = '#FFF'
  return color
})

const onMouseOver = () => {
  isHover.value = true
}

const onMouseLeave = () => {
  isHover.value = false
}

const onClick = (event: MouseEvent) => {
  if (props.disabled) return
  emits('click', event)
}
</script>

<template>
  <component
    :is="tag"
    class="button"
    :class="buttonClass"
    :style="styles"
    :to="to"
    :href="href"
    :disabled="disabled"
    :target="target"
    type="button"
    @click="onClick($event)"
    @mouseover="onMouseOver"
    @mouseleave="onMouseLeave"
  >
    <span v-if="iconName" class="button__icon"><KkIcon :name="iconName" :color="iconColor" /></span>
    <span class="button__label">{{ text }}</span>
  </component>
</template>

<style lang="scss" scoped>
@import "../../../../../stylesheets/biz_spa/foundation/_var.scss";
.button {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  padding: 0 14px;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1;
  text-decoration: none;
  text-align: center;
  white-space: nowrap;
  cursor: pointer;
  transition: .2s;
  &__icon {
    width: 15px;
    margin-right: 8px;
  }
  &--primary {
    border: 1px solid $color-accent-primary;
    background-color: $color-accent-primary;
    color: #FFF;
    &:hover {
      background-color: lighten($color-accent-primary, 5%);
    }
  }
  &--primaryOutlined {
    border: 1px solid;
    background-color: #FFF;
    color: $color-accent-primary;
    &:hover {
      border-color: lighten($color-accent-primary, 5%);
      background-color: lighten($color-accent-primary, 5%);
      color: #FFF;
    }
  }
  &--secondary {
    border: 1px solid $color-base-c;
    background-color: #FFF;
    color: $color-font-negative;
    &:hover {
      border-color: lighten($color-font-negative, 5%);
      background-color: lighten($color-font-negative, 5%);
      color: #FFF;
    }
  }
  &--disabled {
    border: 1px solid;
    background-color: #E3E3E3;
    color: #FFF;
    cursor: not-allowed;
    &:hover {
      border: 1px solid;
      background-color: #E3E3E3;
      color: #FFF;
      cursor: not-allowed;
    }
  }
  &--full {
    width: 100%;
  }
}
</style>
